/**
 * Roadmap collection — typed CRUD on top of the generic store.
 *
 * A RoadmapItem is one milestone on the now/next/later/shipped ladder:
 * a title, the goal it serves, an owner, a timeline, a status, and a
 * completion estimate. The Development tab groups these by stage to tell
 * the engineering delivery story. Modelled on `tasks.ts` / `agents.ts`.
 */

import { db, nowIso, uuid } from './store';
import type { MilestoneStatus, RoadmapItem, RoadmapStage } from './types';
import { seedRoadmap } from './seed';

export async function listRoadmap(): Promise<RoadmapItem[]> {
  return db.list('roadmap');
}

export async function getRoadmapItem(id: string): Promise<RoadmapItem | null> {
  return db.get('roadmap', id);
}

export function subscribeRoadmap(
  fn: (records: RoadmapItem[]) => void,
): () => void {
  return db.subscribe('roadmap', fn);
}

export interface NewRoadmapInput {
  title: string;
  description?: string;
  stage?: RoadmapStage;
  status?: MilestoneStatus;
  goal?: string;
  assignee?: string;
  startDate?: string;
  targetDate?: string;
  progress?: number;
  linkedTicketIds?: string[];
  linkedTaskIds?: string[];
}

/** Create a roadmap milestone. Defaults to a planned `next` item. */
export async function createRoadmapItem(
  input: NewRoadmapInput,
): Promise<RoadmapItem> {
  const ts = nowIso();
  const record: RoadmapItem = {
    id: uuid(),
    title: input.title.trim(),
    description: input.description?.trim() || undefined,
    stage: input.stage ?? 'next',
    status: input.status ?? 'planned',
    goal: input.goal?.trim() || undefined,
    assignee: input.assignee?.trim() || undefined,
    startDate: input.startDate,
    targetDate: input.targetDate,
    progress: input.progress ?? 0,
    linkedTicketIds: input.linkedTicketIds ?? [],
    linkedTaskIds: input.linkedTaskIds ?? [],
    createdAt: ts,
    updatedAt: ts,
  };
  await db.add('roadmap', record);
  return record;
}

/** Patch a roadmap milestone. Re-stamps `updatedAt`. */
export async function updateRoadmapItem(
  id: string,
  patch: Partial<Omit<RoadmapItem, 'id' | 'createdAt'>>,
): Promise<RoadmapItem> {
  return db.update('roadmap', id, { ...patch, updatedAt: nowIso() });
}

export async function deleteRoadmapItem(id: string): Promise<void> {
  await db.remove('roadmap', id);
}

/** Seed the roadmap collection on first run. Idempotent. */
export async function bootstrapRoadmapIfEmpty(): Promise<void> {
  const existing = await db.list('roadmap');
  if (existing.length > 0) return;
  await db.replaceAll('roadmap', seedRoadmap);
}

/** Reset the roadmap collection back to seed data. Useful for dev. */
export async function resetRoadmapToSeed(): Promise<void> {
  await db.replaceAll('roadmap', seedRoadmap);
}
